import { useEffect, useState } from "react";
import { PawPrint, Save, Plus, Trash2, Users, Ticket, CheckCircle2, Clock, DollarSign, RefreshCw, Percent } from "lucide-react";
import DashboardShell from "@/components/hola/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { toast } from "sonner";

const MAX_TIERS = 5;

export default function ManadaPage() {
  return (
    <DashboardShell active="manada">
      {() => <ManadaBody />}
    </DashboardShell>
  );
}

function ManadaBody() {
  const [config, setConfig] = useState({ enabled: false, tiers: [] });
  const tierKey = () => Math.random().toString(36).slice(2, 10);
  const withKeys = (tiers) => (tiers || []).map((t) => ({ ...t, _k: t._k || tierKey() }));
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [report, setReport] = useState(null);

  // Redemption form
  const [rCode, setRCode] = useState("");
  const [rWaiter, setRWaiter] = useState("");
  const [rTicket, setRTicket] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [lastRedeemed, setLastRedeemed] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get("/merchants/me/manada"),
        api.get("/merchants/me/manada/report?days=30"),
      ]);
      setConfig({ enabled: c.data.enabled, tiers: withKeys(c.data.tiers) });
      setReport(r.data);
    } catch {
      toast.error("No se pudo cargar MANADA");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- carga al montar

  const addTier = () => {
    if (config.tiers.length >= MAX_TIERS) {
      toast.info(`Máximo ${MAX_TIERS} tramos`);
      return;
    }
    // Sugerir el siguiente tramo lógico
    const last = config.tiers[config.tiers.length - 1];
    const next = last
      ? { min_people: last.min_people + 2, discount_percent: Math.min(80, last.discount_percent + 5) }
      : { min_people: 3, discount_percent: 5 };
    setConfig((c) => ({ ...c, tiers: [...c.tiers, { ...next, _k: tierKey() }] }));
  };

  const updateTier = (idx, patch) => {
    setConfig((c) => ({
      ...c,
      tiers: c.tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    }));
  };

  const removeTier = (idx) => {
    setConfig((c) => ({ ...c, tiers: c.tiers.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/merchants/me/manada", config);
      setConfig({ enabled: data.enabled, tiers: withKeys(data.tiers) });
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pude guardar");
    } finally {
      setSaving(false);
    }
  };

  const redeem = async (e) => {
    e.preventDefault();
    setRedeeming(true);
    setLastRedeemed(null);
    try {
      const { data } = await api.post("/merchants/me/manada/redeem", {
        code: rCode.trim().toUpperCase(),
        waiter_name: rWaiter.trim(),
        ticket_amount: parseFloat(rTicket),
      });
      setLastRedeemed(data);
      setRCode(""); setRTicket("");
      toast.success(`¡Descuento aplicado! $${data.discount_amount.toFixed(2)} MXN`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pude canjear el código");
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) return <div className="text-sm text-neutral-500">Cargando…</div>;

  const stats = report?.stats || { total: 0, active: 0, redeemed: 0, expired: 0, total_discount_mxn: 0, total_tickets_mxn: 0 };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold flex items-center gap-1"><PawPrint className="w-3 h-3" /> MANADA</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">
          Descuentos grupales (MANADA)
        </h1>
        <p className="text-sm text-neutral-500 mt-2 leading-relaxed max-w-2xl">
          Ofrece un mejor descuento a quienes vengan en grupo. Entre más grande la MANADA, más descuento. Perfecto para restaurantes, cafeterías, salones de belleza y fondas.
        </p>
      </div>

      {/* Toggle activar */}
      <div className="rounded-2xl bg-gradient-to-br from-[#FF007A]/10 to-[#0055FF]/10 border border-[#FF007A]/20 p-5 flex items-center justify-between gap-4">
        <div>
          <div className="font-heading font-extrabold text-base">MANADA está {config.enabled ? "ACTIVA" : "PAUSADA"}</div>
          <div className="text-xs text-neutral-500 mt-1">
            {config.enabled
              ? "Los usuarios verán tu comercio en la lista MANADA de la PWA."
              : "Actívala para que los usuarios en grupo puedan generar códigos de descuento."}
          </div>
        </div>
        <Switch
          checked={config.enabled}
          onCheckedChange={(v) => setConfig((c) => ({ ...c, enabled: v }))}
          data-testid="manada-toggle"
        />
      </div>

      {/* Tramos editables */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading font-extrabold text-lg">Tramos de descuento</h2>
            <p className="text-xs text-neutral-500 mt-0.5">Entre más personas, más descuento. Hasta {MAX_TIERS} tramos.</p>
          </div>
          <div className="text-xs text-neutral-500">{config.tiers.length} / {MAX_TIERS}</div>
        </div>

        <div className="space-y-2" data-testid="manada-tiers">
          {config.tiers.length === 0 && (
            <div className="text-center py-6 text-sm text-neutral-500 border border-dashed border-neutral-300 rounded-xl">
              Aún no has definido tramos. Agrega el primero abajo.
            </div>
          )}
          {config.tiers.map((t, i) => (
            <div key={t._k} className="flex items-center gap-3 bg-neutral-50 rounded-xl p-3 border border-neutral-200" data-testid={`manada-tier-${i}`}>
              <div className="text-lg font-heading font-extrabold text-neutral-400 w-8">#{i + 1}</div>
              <div className="flex-1 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Personas mínimo</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <Users className="w-4 h-4 text-neutral-400" />
                    <Input
                      type="number"
                      min={2}
                      max={99}
                      value={t.min_people}
                      onChange={(e) => updateTier(i, { min_people: parseInt(e.target.value) || 2 })}
                      className="h-10 rounded-xl"
                      data-testid={`manada-tier-people-${i}`}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Descuento</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <Percent className="w-4 h-4 text-neutral-400" />
                    <Input
                      type="number"
                      min={1}
                      max={80}
                      value={t.discount_percent}
                      onChange={(e) => updateTier(i, { discount_percent: parseInt(e.target.value) || 1 })}
                      className="h-10 rounded-xl"
                      data-testid={`manada-tier-pct-${i}`}
                    />
                  </div>
                </div>
              </div>
              <button
                onClick={() => removeTier(i)}
                className="text-red-500 hover:text-red-700 p-2"
                aria-label="Eliminar tramo"
                data-testid={`manada-tier-remove-${i}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {config.tiers.length < MAX_TIERS && (
          <button
            onClick={addTier}
            className="mt-3 w-full border-2 border-dashed border-neutral-300 hover:border-[#FF007A] rounded-xl py-3 text-sm font-bold text-neutral-500 hover:text-[#FF007A] transition-colors flex items-center justify-center gap-2"
            data-testid="manada-add-tier"
          >
            <Plus className="w-4 h-4" /> Agregar tramo
          </button>
        )}

        <div className="mt-5 flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-full bg-[#0055FF] hover:bg-[#0044cc] font-bold h-11 px-6"
            data-testid="manada-save-btn"
          >
            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar configuración"}
          </Button>
        </div>
      </div>

      {/* Sección Canjear código (para meseros) */}
      <div className="rounded-2xl border-2 border-[#FF007A]/30 bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <Ticket className="w-5 h-5 text-[#FF007A]" />
          <h2 className="font-heading font-extrabold text-lg">Canjear código MANADA</h2>
        </div>
        <p className="text-xs text-neutral-500 mb-4">Cuando el grupo llegue con su código, escríbelo aquí junto con tu nombre y el monto del ticket. El descuento se aplica en automático.</p>

        <form onSubmit={redeem} className="grid md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Código MANADA</Label>
            <Input
              value={rCode}
              onChange={(e) => setRCode(e.target.value.toUpperCase())}
              placeholder="MAN-XXXXXX"
              className="mt-1 h-11 rounded-xl font-mono uppercase tracking-wider"
              maxLength={20}
              required
              data-testid="manada-redeem-code"
            />
          </div>
          <div>
            <Label className="text-xs">Tu nombre (mesero / staff)</Label>
            <Input
              value={rWaiter}
              onChange={(e) => setRWaiter(e.target.value)}
              placeholder="Ej: Carlos"
              className="mt-1 h-11 rounded-xl"
              maxLength={120}
              required
              data-testid="manada-redeem-waiter"
            />
          </div>
          <div>
            <Label className="text-xs">Monto ticket (MXN, sin descuento)</Label>
            <Input
              type="number"
              min={1}
              step="0.01"
              value={rTicket}
              onChange={(e) => setRTicket(e.target.value)}
              placeholder="450.00"
              className="mt-1 h-11 rounded-xl"
              required
              data-testid="manada-redeem-ticket"
            />
          </div>
          <div className="md:col-span-3">
            <Button
              type="submit"
              disabled={redeeming}
              className="w-full h-12 rounded-full bg-[#FF007A] hover:bg-[#e60070] font-bold"
              data-testid="manada-redeem-submit"
            >
              <CheckCircle2 className="w-4 h-4" /> {redeeming ? "Canjeando…" : "Aplicar descuento"}
            </Button>
          </div>
        </form>

        {lastRedeemed && (
          <div className="mt-4 rounded-xl bg-green-50 border-2 border-green-400 p-4" data-testid="manada-last-redeemed">
            <div className="font-heading font-extrabold text-lg text-green-800">
              ✓ Descuento aplicado a {lastRedeemed.user_name}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
              <div><div className="text-xs text-neutral-500">Grupo</div><div className="font-bold">{lastRedeemed.group_size} personas</div></div>
              <div><div className="text-xs text-neutral-500">Descuento</div><div className="font-bold">{lastRedeemed.discount_percent}%</div></div>
              <div><div className="text-xs text-neutral-500">Ticket original</div><div className="font-bold">${lastRedeemed.ticket_amount.toFixed(2)}</div></div>
              <div><div className="text-xs text-neutral-500">Total a cobrar</div><div className="font-bold text-[#FF007A] text-lg">${lastRedeemed.total_after.toFixed(2)}</div></div>
            </div>
          </div>
        )}
      </div>

      {/* Reporte */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#0055FF]" />
            <h2 className="font-heading font-extrabold text-lg">Reporte MANADA (últimos 30 días)</h2>
          </div>
          <Button onClick={loadAll} variant="outline" className="rounded-full h-9 text-xs">
            <RefreshCw className="w-3 h-3" /> Actualizar
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <MiniStat label="Códigos" value={stats.total} icon={Ticket} color="bg-neutral-100" />
          <MiniStat label="Canjeados" value={stats.redeemed} icon={CheckCircle2} color="bg-green-100 text-green-800" />
          <MiniStat label="Activos" value={stats.active} icon={Clock} color="bg-amber-100 text-amber-800" />
          <MiniStat label="Descuento total" value={`$${stats.total_discount_mxn.toFixed(0)}`} icon={Percent} color="bg-[#FF007A]/10 text-[#FF007A]" />
          <MiniStat label="Ventas MANADA" value={`$${stats.total_tickets_mxn.toFixed(0)}`} icon={DollarSign} color="bg-[#0055FF]/10 text-[#0055FF]" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="manada-report-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
                <th className="py-2 font-bold">Código</th>
                <th className="py-2 font-bold">Cliente</th>
                <th className="py-2 font-bold text-center">Grupo</th>
                <th className="py-2 font-bold text-center">Desc %</th>
                <th className="py-2 font-bold">Estatus</th>
                <th className="py-2 font-bold">Fecha</th>
                <th className="py-2 font-bold">Canjeó (mesero)</th>
                <th className="py-2 font-bold text-right">Ticket</th>
                <th className="py-2 font-bold text-right">Descuento $</th>
              </tr>
            </thead>
            <tbody>
              {(report?.codes || []).map((c) => (
                <tr key={c.code} className="border-b border-neutral-100">
                  <td className="py-2 font-mono text-xs">{c.code}</td>
                  <td className="py-2">{c.user_name}</td>
                  <td className="py-2 text-center">{c.group_size}</td>
                  <td className="py-2 text-center">{c.discount_percent}%</td>
                  <td className="py-2">
                    <StatusPill status={c.status} />
                  </td>
                  <td className="py-2 text-xs text-neutral-500">
                    {new Date(c.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="py-2 text-neutral-700">{c.redeemed_by_waiter || <span className="text-neutral-400 italic">—</span>}</td>
                  <td className="py-2 text-right">{c.redeemed_ticket_amount ? `$${c.redeemed_ticket_amount.toFixed(2)}` : "—"}</td>
                  <td className="py-2 text-right font-bold text-[#FF007A]">{c.redeemed_discount_amount ? `$${c.redeemed_discount_amount.toFixed(2)}` : "—"}</td>
                </tr>
              ))}
              {(!report?.codes || report.codes.length === 0) && (
                <tr>
                  <td colSpan={9} className="text-center py-8 text-sm text-neutral-500">
                    Aún no hay códigos MANADA generados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, color }) {
  return (
    <div className={`rounded-xl px-3 py-2.5 ${color}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-70 flex items-center gap-1"><Icon className="w-3 h-3" />{label}</div>
      <div className="font-heading font-extrabold text-xl mt-1 leading-none">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active:   { label: "Activo",   cls: "bg-amber-100 text-amber-700 border-amber-300" },
    redeemed: { label: "Canjeado", cls: "bg-green-100 text-green-700 border-green-300" },
    expired:  { label: "Expiró",   cls: "bg-neutral-100 text-neutral-500 border-neutral-300" },
  };
  const s = map[status] || map.expired;
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>;
}
